import { ReactNode, useMemo } from 'react';
import { Feature, FeatureCollection } from 'geojson';
import { Marker, MarkerProps as LeafletMarkerProps, Polygon } from 'react-leaflet';
import flip from '@turf/flip';
import { Map2Props, MarkerProps } from '@Types';
import { reactComponentToLeafletIcon } from './utils';
import L, { LeafletEventHandlerFnMap } from 'leaflet';
import { DivIcon } from 'leaflet';
import { Popup } from './Popups/Popup';

const leafletIcons = {
  pink: new L.Icon({
    iconUrl: '/assets/MapIcons/marker-icon-2x-af-pink.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  }),
  grey: new L.Icon({
    iconUrl:
      'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-grey.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  }),
  default: new L.Icon({
    iconUrl:
      'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  }),
};

export const DisplayFeatureCollection = ({
  featureCollection,
  markers,
  popupFunctions,
  onMarkerClick,
}: {
  featureCollection: FeatureCollection;
  markers?: {
    [key: string]: MarkerProps;
  };
  popupFunctions?: {
    [key: string]: (...args: any[]) => ReactNode;
  };
  onMarkerClick?: Map2Props['onMarkerClick'];
}) => {
  const icons = useMemo(() => {
    let tempIcons: { [key: string]: DivIcon } = {};
    if (markers) {
      for (const [name, marker] of Object.entries(markers)) {
        if (marker.component) {
          tempIcons[name] = reactComponentToLeafletIcon(marker.component, marker.backgroundColor);
        } else if (marker.leafletIconName) {
          tempIcons[name] = leafletIcons[marker.leafletIconName];
        }
      }
    }
    return tempIcons;
  }, [markers]);

  return (
    <>
      {featureCollection.features.map((feature: Feature, i) => {
        switch (feature.geometry.type) {
          case 'Polygon':
            // @ts-ignore
            return <Polygon key={i} positions={flip(feature).geometry.coordinates} />;
          case 'MultiPolygon':
            return (
              <Polygon
                key={i}
                // @ts-ignore
                positions={flip(feature).geometry.coordinates}
                attribution={feature.properties?.attribution}
              />
            );
          case 'Point':
            let featureMarker = undefined;
            let featurePopup = undefined;
            let popFunction = undefined;
            let finalMarker: LeafletMarkerProps = {
              position: [
                feature.geometry.coordinates[1] as number,
                feature.geometry.coordinates[0] as number,
              ],
            };
            if (feature.properties) {
              // This will be a string. featurePopup will remain as undefined if not set
              featureMarker = feature.properties.marker;
              // This will either be a string or null. featurePopup will remain as undefined if not set
              featurePopup = feature.properties.popup;
            }

            // Setup marker
            if (markers) {
              let icon = icons[featureMarker ? featureMarker : 'default'];
              let marker = markers[featureMarker ? featureMarker : 'default'];

              if (marker?.zIndexOffset) {
                finalMarker.zIndexOffset = marker.zIndexOffset;
              }

              if (icon) {
                finalMarker.icon = icon;
              }
            }

            // Setup popup
            if ((featurePopup || featurePopup === undefined) && popupFunctions) {
              popFunction = popupFunctions[featurePopup ? featurePopup : 'default'];
            }

            const eventHandlers: LeafletEventHandlerFnMap = {};
            if (onMarkerClick) {
              eventHandlers.click = (e) => {
                onMarkerClick(feature.properties, e);
              };
            }

            return (
              <Marker key={i} {...finalMarker} eventHandlers={eventHandlers}>
                <Popup featureProperties={feature.properties} popupFunction={popFunction} />
              </Marker>
            );
          default:
            return null;
        }
      })}
    </>
  );
};
